import classNames from 'classnames/bind';
import { nanoid } from 'nanoid';

import { IconList, Svg } from 'components/svg/Svg';
import { FormError } from 'containers/forms/FormError';

import { Message } from './Message';

import s from './TextInput.module.scss';

const c = classNames.bind(s);

type TextInputTypes =
  | 'email'
  | 'number'
  | 'password'
  | 'search'
  | 'tel'
  | 'text'
  | 'time'
  | 'url'
  | 'week'
  | 'file';

export type TextInputProps = {
  inputType?: TextInputTypes;
  floatLabel?: boolean;
  label?: string;
  message?: string;
  error?: boolean;
  icon?: IconList;
  boldLabel?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>;

export const TextInput = ({
  inputType = 'text',
  className,
  label,
  message,
  error,
  floatLabel = false,
  id,
  required,
  placeholder,
  icon,
  boldLabel,
  ...rest
}: TextInputProps) => {
  const inputId = id ?? nanoid(10);
  const renderedLabel = required && label ? `${label} *` : label;

  return (
    <div
      className={c(s.textInput, className, inputType, {
        icon,
        hasLabel: typeof renderedLabel === 'string' && renderedLabel.length > 0,
        boldLabel,
        error,
        hideFloatLabel: !floatLabel,
      })}
    >
      {label && (
        <label className={s.textInput__label} htmlFor={inputId}>
          {renderedLabel}
        </label>
      )}
      <div className={s.textInput__inner}>
        {icon && (
          <span className={s.textInput__icon}>
            <Svg id={icon} />
          </span>
        )}
        <input
          type={inputType}
          className={c(s.textInput__input)}
          {...rest}
          id={inputId}
          placeholder={
            !placeholder && renderedLabel === 'string'
              ? renderedLabel
              : placeholder ?? undefined
          }
          aria-describedby={message ? `${inputId}-error}` : ''}
          required={required}
        />
      </div>
      {message && <Message id={`${inputId}-error`}>{message}</Message>}
      {rest.name && <FormError name={rest.name} placeholder={label} />}
    </div>
  );
};
